'use client'

import {
  inputBase,
  inputDisabled,
  inputError,
  inputFocus,
  inputOutlined,
} from '@/core/constants/class-names'
import { cn } from '@/core/lib/utils/cn'
import { dateService } from '@/core/services/date'
import 'flatpickr/dist/themes/airbnb.css'
import { Clock } from 'lucide-react'
import { useMemo } from 'react'
import Flatpickr, { DateTimePickerHandle } from 'react-flatpickr'
import { createInput } from './input-factory'

type Props = {
  name: string
  value?: Date
  onChange?: (value: Date) => void
  className?: string
  minTime?: Date | string
  error?: string | boolean
  errorMessage?: string
  ref?: React.Ref<DateTimePickerHandle>
}

const BaseTimeInput: React.FC<Props> = ({
  name,
  value,
  onChange,
  className = '',
  minTime,
  error,
  errorMessage,
  ref,
  ...rest
}) => {
  const hasError = Boolean(error)

  const options = useMemo(
    () => ({
      minTime,
      noCalendar: true,
      dateFormat: 'H:i',
      disableMobile: true,
      allowInput: true,
      enableTime: true,
    }),
    [minTime],
  )

  let renderedValue = dateService.timeToHI(new Date())

  if (value && value instanceof Date && !isNaN(value.getTime())) {
    renderedValue = dateService.timeToHI(value)
  } else if (typeof value === 'string') {
    renderedValue = value
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-1 rounded',
        hasError && inputError,
      )}
    >
      <div className='relative'>
        <Flatpickr
          {...rest}
          ref={ref}
          name={name}
          value={renderedValue}
          onChange={([date]) => {
            const newTime = dateService.roundMinutes(date)
            onChange?.(newTime)
          }}
          className={cn(
            'px-1 disabled:cursor-not-allowed',
            inputDisabled,
            inputBase,
            inputFocus,
            inputOutlined,
            className,
            'ps-8',
          )}
          options={options}
        />
        <Clock className='absolute start-2 top-1/2 h-4 w-4 -translate-y-1/2' />
      </div>
      {hasError && errorMessage && (
        <p className='text-red text-sm'>{errorMessage}</p>
      )}
    </div>
  )
}
export const TimeInput = createInput(BaseTimeInput, ['label'])
export const FormTimeInput = createInput(BaseTimeInput, ['form', 'label'])
